import { Box, Typography, useColorScheme } from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";

interface HeaderProps {
  accentColor?: string;
}

export default function Header({ accentColor = "rgba(120, 119, 198, 0.8)" }: HeaderProps) {
  const theme = useTheme();
  const { mode } = useColorScheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = mode === "dark";

  // Extract RGB values from rgba string to create different opacity variants
  const rgbMatch = accentColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  const rgb = rgbMatch ? `${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}` : "120, 119, 198";

  return (
    <Box
      sx={{
        textAlign: "center",
        py: 6,
        position: "relative",
      }}
    >
      {/* Magical glow effect behind title */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "600px",
          height: "200px",
          background: isDark
            ? `radial-gradient(ellipse, rgba(${rgb}, 0.2) 0%, transparent 70%)`
            : `radial-gradient(ellipse, rgba(${rgb}, 0.1) 0%, transparent 70%)`,
          filter: "blur(40px)",
          zIndex: -1,
        }}
      />

      <Typography
        level={isMobile ? "h3" : "h1"}
        sx={{
          background: isDark
            ? "linear-gradient(45deg, #ffffff 0%, #e0e7ff 50%, #c7d2fe 100%)"
            : "linear-gradient(45deg, #1e293b 0%, #334155 50%, #475569 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textShadow: isDark
            ? `0 0 30px rgba(${rgb}, 0.5)`
            : `0 0 20px rgba(${rgb}, 0.3)`,
          letterSpacing: "0.1em",
          mb: 2,
          fontWeight: 300,
        }}
      >
        TIMELESS' ARIA
      </Typography>

      <Typography
        level="body-lg"
        sx={{
          color: isDark ? "rgba(255, 255, 255, 0.8)" : "rgba(30, 41, 59, 0.8)",
          textTransform: "uppercase",
          letterSpacing: "0.2em",
          mb: 1,
          fontSize: isMobile ? "0.9rem" : "1.1rem",
        }}
      >
        A High Fantasy War Epic
      </Typography>

      <Typography
        level="body-md"
        sx={{
          color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(30, 41, 59, 0.6)",
          textTransform: "uppercase",
          letterSpacing: "0.15em",
          fontSize: isMobile ? "0.8rem" : "1rem",
        }}
      >
        By Mostafa A. M. Elbehery & Aly M. A. Said
      </Typography>

      {/* Decorative elements */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 4,
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: "60px",
            height: "1px",
            background: isDark
              ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)"
              : "linear-gradient(90deg, transparent, rgba(30, 41, 59, 0.4), transparent)",
          }}
        />
        <Box
          sx={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: `radial-gradient(circle, rgba(${rgb}, 0.8) 0%, transparent 70%)`,
            boxShadow: `0 0 10px rgba(${rgb}, 0.6)`,
          }}
        />
        <Box
          sx={{
            width: "60px",
            height: "1px",
            background: isDark
              ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)"
              : "linear-gradient(90deg, transparent, rgba(30, 41, 59, 0.4), transparent)",
          }}
        />
      </Box>
    </Box>
  );
}
